import * as yup from "yup";
import { addDotsSeperator } from "utils/addDotsSeperator";

export const DEFAULT_DECIMAL_PLACES = 2;

const sanitizeDecimalInput = (value) =>
  value
    ?.toString()
    ?.replace(/[^0-9.,]/g, "")
    ?.replace(/\s/g, "") || "";

const getIntegerDisplayValue = (value) => {
  if (!value) {
    return "";
  }

  return addDotsSeperator(value);
};

export const parseDecimalInputValue = (
  value,
  { decimalPlaces = DEFAULT_DECIMAL_PLACES, previousValue, inputType } = {},
) => {
  const sanitizedValue = sanitizeDecimalInput(value);

  if (!sanitizedValue) {
    return "";
  }

  const previousRawValue =
    previousValue === null || previousValue === undefined
      ? ""
      : `${previousValue}`;
  const isDeletingFromGroupedInteger =
    inputType?.startsWith("delete") &&
    sanitizedValue.includes(".") &&
    !sanitizedValue.includes(",") &&
    previousRawValue.length > 0 &&
    !previousRawValue.includes(".");

  if (isDeletingFromGroupedInteger) {
    return sanitizedValue.replace(/[.,]/g, "");
  }

  const commaIndex = sanitizedValue.indexOf(",");

  if (commaIndex !== -1) {
    const integerPart = sanitizedValue
      .slice(0, commaIndex)
      .replace(/[.,]/g, "");
    const decimalPart = sanitizedValue
      .slice(commaIndex + 1)
      .replace(/[.,]/g, "")
      .slice(0, decimalPlaces);
    const normalizedIntegerPart =
      integerPart ||
      (decimalPart.length > 0 || sanitizedValue.startsWith(",") ? "0" : "");

    if (sanitizedValue.endsWith(",") && decimalPart.length === 0) {
      return `${normalizedIntegerPart}.`;
    }

    return decimalPart.length > 0
      ? `${normalizedIntegerPart}.${decimalPart}`
      : normalizedIntegerPart;
  }

  const dotMatches = sanitizedValue.match(/\./g) || [];

  if (dotMatches.length === 1) {
    const dotIndex = sanitizedValue.indexOf(".");
    const integerPart = sanitizedValue.slice(0, dotIndex).replace(/[.,]/g, "");
    const decimalPart = sanitizedValue.slice(dotIndex + 1).replace(/[.,]/g, "");
    const normalizedIntegerPart =
      integerPart ||
      (decimalPart.length > 0 || sanitizedValue.startsWith(".") ? "0" : "");

    if (sanitizedValue.endsWith(".")) {
      return `${normalizedIntegerPart}.`;
    }

    if (decimalPart.length > 0 && decimalPart.length <= decimalPlaces) {
      return `${normalizedIntegerPart}.${decimalPart}`;
    }
  }

  return sanitizedValue.replace(/[.,]/g, "");
};

export const formatDecimalDisplayValue = (
  value,
  { decimalPlaces = DEFAULT_DECIMAL_PLACES } = {},
) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const rawValue =
    typeof value === "number" ? value.toString() : `${value}`.trim();

  if (rawValue.length === 0) {
    return "";
  }

  const normalizedValue = rawValue.replace(/\s/g, "");
  const hasTrailingDecimalSeparator = normalizedValue.endsWith(".");
  const [integerPart = "", decimalPart = ""] = normalizedValue.split(".");
  const normalizedIntegerPart = integerPart.replace(/\D/g, "");
  const normalizedDecimalPart = decimalPart
    .replace(/\D/g, "")
    .slice(0, decimalPlaces);
  const formattedIntegerPart = getIntegerDisplayValue(
    normalizedIntegerPart ||
      (normalizedDecimalPart || hasTrailingDecimalSeparator ? "0" : ""),
  );

  if (hasTrailingDecimalSeparator && normalizedDecimalPart.length === 0) {
    return `${formattedIntegerPart},`;
  }

  return normalizedDecimalPart.length > 0
    ? `${formattedIntegerPart},${normalizedDecimalPart}`
    : formattedIntegerPart;
};

export const parseDecimalValueToNumber = (
  value,
  { decimalPlaces = DEFAULT_DECIMAL_PLACES } = {},
) => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  if (typeof value === "number") {
    return Number.isNaN(value) ? undefined : value;
  }

  const normalizedValue = parseDecimalInputValue(value, { decimalPlaces });

  if (!normalizedValue) {
    return undefined;
  }

  const parsedValue = Number(normalizedValue);

  return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

export const buildDecimalYupSchema = ({
  decimalPlaces = DEFAULT_DECIMAL_PLACES,
  validationMessage,
} = {}) => {
  const resolvedValidationMessage =
    validationMessage || "Το πεδίο πρέπει να είναι αριθμός";

  return yup
    .number()
    .transform((value, originalValue) => {
      return parseDecimalValueToNumber(originalValue, { decimalPlaces });
    })
    .typeError(resolvedValidationMessage);
};
